import { Injectable } from '@angular/core';
import { DaysLong, DaysShort, MonthsLong, MonthsShort } from '@models/dates.model';

@Injectable({
  providedIn: 'root'
})
export class DateHelperService {

  constructor() { }

  getDate(d?: Date | string) {
    const date = new Date(d);
    return isNaN(date.getTime()) ? new Date() : date;
  }

  getRemainingTime(inType: 'Y' | 'M' | 'D' | 'H' | 'm' | 's' | 'ms', fromDate: string | Date, toDate: string | Date = new Date(), addExtra: number = 0): number {
    const from = this.getDate(fromDate);
    const to = this.getDate(toDate);
    const diff = (from.getTime() - to.getTime()) / 1000;
    let remain = 0;
    if (inType === 's') remain = Math.floor(diff);
    if (inType === 'm') remain = Math.floor(diff / 60);
    if (inType === 'H') remain = Math.floor(diff / (60 * 60));
    if (inType === 'D') remain = Math.floor(diff / (60 * 60 * 24));
    if (inType === 'M') remain = ((from.getFullYear() - to.getFullYear()) * 12) + (from.getMonth() - to.getMonth());
    if (inType === 'Y') remain = from.getFullYear() - to.getFullYear();
    return remain + addExtra;
  }

  getRelativeTimeRemaining(endDate: string | Date, inType?: { Y?: boolean; M?: boolean; D?: boolean; H?: boolean; m?: boolean; s?: boolean; }): { Y: number; M: number; D: number; H: number; m: number; s: number; } {

    let remaining = this.getRemainingTime('s', endDate, new Date());
    const isNegative = remaining < 0;
    if (isNegative) remaining = Math.abs(remaining);

    const units = {
      Y: 60 * 60 * 24 * 365,
      M: 60 * 60 * 24 * 30,
      D: 60 * 60 * 24,
      H: 60 * 60,
      m: 60,
      s: 1
    } as const;

    const result = {} as { Y: number; M: number; D: number; H: number; m: number; s: number; };

    for (const [key, value] of Object.entries(units) as [keyof typeof units, number][]) {
      if (inType && !inType[key]) continue;
      result[key] = Math.floor(remaining / value) * (isNegative ? -1 : 1);
      remaining %= value;
    }
    return result;
  }

  formatDate(d: Date | string, format: string, addExtra?: { day?: number; month?: number; year?: number; }): string {
    const date = this.getDate(d);
    const year = date.getFullYear() + (addExtra?.year || 0);
    const month = date.getMonth() + (addExtra?.month || 0);
    const day = date.getDay() + (addExtra?.day || 0);
    const nth = (num: number): string => {
      const suffixes = ['th', 'st', 'nd', 'rd'];
      const v = num % 100;
      return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
    };
    const map: Record<string, string> = {
      YYYY: year.toString(),
      YY: year.toString().slice(-2),
      MMMM: MonthsLong[month],
      MMM: MonthsShort[month],
      MM: String(month + 1).padStart(2, '0'),
      DDDD: DaysLong[day],
      DDD: DaysShort[day],
      Do: `${date.getDate()}${nth(date.getDate())}`,
      DD: String(date.getDate()).padStart(2, '0'),
      HH: String(date.getHours()).padStart(2, '0'),
      mm: String(date.getMinutes()).padStart(2, '0'),
      ss: String(date.getSeconds()).padStart(2, '0')
    };
    return format.replace(/YYYY|YY|MMMM|MMM|MM|DDDD|DDD|Do|DD|HH|mm|ss/g, matched => map[matched]);
  }

  getTimeAgo(d: Date | string) {
    const targetDate = this.getUtcDate(d);
    const diffMs = targetDate.getTime() - Date.now();
    const absMs = Math.abs(diffMs);
    const isFuture = diffMs > 0;

    const seconds = Math.round(absMs / 1000);
    const minutes = Math.round(absMs / (1000 * 60));
    const hours = Math.round(absMs / (1000 * 60 * 60));
    const days = Math.round(absMs / (1000 * 60 * 60 * 24));
    const months = Math.round(this.getMonthDiff(new Date(), targetDate));
    const years = Math.round(Math.abs(months) / 12);

    let text: string;
    if (seconds <= 44) text = 'a few seconds';
    else if (seconds <= 89) text = 'a minute';
    else if (minutes <= 44) text = `${minutes} minutes`;
    else if (minutes <= 89) text = 'an hour';
    else if (hours <= 21) text = `${hours} hours`;
    else if (hours <= 35) text = 'a day';
    else if (days <= 25) text = `${days} days`;
    else if (days <= 45) text = 'a month';
    else if (Math.abs(months) <= 10) text = `${Math.abs(months)} months`;
    else if (Math.abs(months) <= 17) text = 'a year';
    else text = `${years} years`;

    return isFuture ? `in ${text}` : `${text} ago`;
  }

  private getUtcDate(d: Date | string): Date {
    if (d instanceof Date) return new Date(d.getTime());
    const dateString = String(d || '').trim();
    if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(dateString)) return this.getDate(dateString);

    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/);

    if (match) {
      const [, year, month, day, hour = '0', minute = '0', second = '0', millisecond = '0'] = match;
      return new Date(Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        Number(millisecond.padEnd(3, '0'))
      ));
    }

    return this.getDate(d);
  }

  private getMonthDiff(fromDate: Date, toDate: Date): number {
    const wholeMonthDiff = ((toDate.getFullYear() - fromDate.getFullYear()) * 12) + (toDate.getMonth() - fromDate.getMonth());
    const anchor = new Date(fromDate.getTime());
    anchor.setMonth(anchor.getMonth() + wholeMonthDiff);

    const anchorDiff = toDate.getTime() - anchor.getTime();
    const nextAnchor = new Date(anchor.getTime());
    nextAnchor.setMonth(nextAnchor.getMonth() + (anchorDiff < 0 ? -1 : 1));

    return Math.abs(wholeMonthDiff + (anchorDiff / Math.abs(nextAnchor.getTime() - anchor.getTime())));
  }

}
